import type {
  IPathAnalyticParameter,
  IPageAnalyticParameter,
  IBaseAnalyticParameter,
} from '~/modules/analytics/types';
import type { PathAnalyticsQueryVariables } from '~/api/graphql/queries/pathAnalytic.generated';
import type { PageAnalyticsQueryVariables } from '~/api/graphql/queries/pageAnalytic.generated';
import type { IBaseCampaignVersion } from '~/modules/campaign/types';
import type { CampaignAnalyticInput } from '~/types/graphql';

import { formatDayjs, getEndOfDayBy } from '~/presentation/ui';
import { EAnalyticMode } from '~/modules/analytics/types';
import { getAnalyticInterval } from '~/modules/analytics';
import { dayjsTz } from '~/utils/dayjs';

export { numberWithCommas, numberWithCommasLean } from '~/utils/number';

export const roundedNumber = (value: string | number, decimal: number) => {
  return Number(Number(value).toFixed(decimal));
};

export const getIncrease = (current: number, previous: number) => {
  if (previous === 0 && current === 0) return 0;
  if (previous === 0 && current > 0) return 100;
  if (previous > 0 && current === 0) return -100;

  return roundedNumber(((current - previous) / previous) * 100, 2);
};

const getFilterByPageOrLocation = (data: {
  shopifyPageID?: string;
  locationPath?: string;
  isEnabledFilterByShopifyPageId: boolean;
}) => {
  const { shopifyPageID, locationPath, isEnabledFilterByShopifyPageId } = data;
  if (isEnabledFilterByShopifyPageId) {
    return shopifyPageID ? { shopifyPageID } : {};
  }
  return locationPath ? { locationPath } : {};
};

const getFilterByGroupBy = (isEnabledFilterByShopifyPageId: boolean) => {
  if (isEnabledFilterByShopifyPageId) {
    return { groupBy: 'SHOPIFY_PAGE_ID' as const };
  }
  return { groupBy: 'LOCATION_PATH' as const };
};

export const getPageAnalyticMetricQueryParams = (
  params: IPageAnalyticParameter,
): PageAnalyticsQueryVariables => {
  const {
    dateRange,
    isFetchMetricByInterval,
    mode,
    includeChildrenStats,
    shopifyPageID,
    locationPath,
    isEnabledFilterByShopifyPageId,
  } = params;

  const dateWhere = getDateQueryVariable(dateRange);

  // Convert dateGTE to start of hour(case date by version of campaign)
  if (dateWhere.dateGTE) {
    dateWhere.dateGTE = dayjsTz(dateWhere.dateGTE).startOf('hour').format();
  }

  const interval = isFetchMetricByInterval
    ? getAnalyticInterval(dateRange.startDate, dateRange.endDate)
    : undefined;

  const queryVariable: PageAnalyticsQueryVariables = {
    input: {
      mode: mode ?? EAnalyticMode.ALL_SESSION,
      ...getFilterByGroupBy(isEnabledFilterByShopifyPageId),
      ...dateWhere,
      ...(isFetchMetricByInterval && interval ? { interval } : {}),
      ...(includeChildrenStats ? { includeChildrenStats } : {}),
      ...getFilterByPageOrLocation({ shopifyPageID, locationPath, isEnabledFilterByShopifyPageId }),
    },
  };
  return queryVariable;
};

export const getPathAnalyticMetricQueryParams = (
  params: IPathAnalyticParameter,
): PathAnalyticsQueryVariables => {
  const {
    dateRange,
    mode,
    shopifyPageID,
    campaignVariantID,
    campaignVersionID,
    locationPath,
    isEnabledFilterByShopifyPageId,
  } = params;
  const dateWhere = getDateQueryVariable(dateRange);

  const queryVariable: PathAnalyticsQueryVariables = {
    input: {
      mode: mode ?? EAnalyticMode.ALL_SESSION,
      ...dateWhere,
      ...(campaignVariantID ? { campaignVariantID } : {}),
      ...(campaignVersionID ? { campaignVersionID } : {}),
      ...getFilterByPageOrLocation({ shopifyPageID, isEnabledFilterByShopifyPageId }),
      locationPath,
    },
  };
  return queryVariable;
};

// TODO: refactor this function
export const getDateQueryVariable = (
  dateRange: IPageAnalyticParameter['dateRange'],
  versions?: IBaseCampaignVersion[],
  isGroup = false,
  format?: string,
): Pick<
  CampaignAnalyticInput,
  'campaignVersionID' | 'dateGTE' | 'dateLTE' | 'campaignGroupVersionID'
> => {
  let additionalQuery = {};

  const campaignGroupVersion = versions?.find((version) => version.version === dateRange.alias);
  const campaignGroupVersionID = campaignGroupVersion?.id;
  const campaignVersion = versions?.find((version) => version.version === dateRange.alias);
  const campaignVersionID = campaignVersion?.id;

  if (isGroup && campaignGroupVersionID) {
    additionalQuery = { campaignGroupVersionID };
  } else if (!isGroup && campaignVersionID) {
    additionalQuery = { campaignVersionID };
  }

  const dateRangeQuery = getDateRangeQueryVariable(dateRange, format);

  return {
    ...dateRangeQuery,
    ...additionalQuery,
  };
};

export const getDateRangeQueryVariable = (
  dateRange: IBaseAnalyticParameter['dateRange'],
  format?: string,
) => {
  const isInTheDay = dateRange.startDate.isSame(dateRange.endDate);
  if (isInTheDay) {
    return {
      dateGTE: formatDayjs(dateRange.startDate, false, format),
      dateLTE: getEndOfDayBy(dateRange.endDate, format),
    };
  }

  return {
    dateGTE: formatDayjs(dateRange.startDate, false, format),
    dateLTE: formatDayjs(dateRange.endDate, true, format),
  };
};
